import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface LandingPricingProps {
  onSelectPlan?: (tierId: string) => void;
  brandPrimary: string;
  brandSecondary: string;
  brandDark: string;
  fadeInUp: any;
}

export const LandingPricing: React.FC<LandingPricingProps> = ({
  onSelectPlan,
  brandPrimary,
  brandSecondary,
  brandDark,
  fadeInUp,
}) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

  return (
    <section id="pricing" style={{ padding: '6rem 5%', maxWidth: '1200px', margin: '0 auto' }}>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
        <div style={{ display: 'flex', gap: '4rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <style>{`
            @media (min-width: 1000px) {
              .pricing-container { flex-wrap: nowrap !important; }
            }
          `}</style>
          <div className="pricing-container" style={{ display: 'flex', width: '100%', gap: '4rem', flexWrap: 'wrap' }}>
            {/* Left Column: Dark Solid Block (Trial info) */}
            <div
              style={{
                flex: '0 0 300px',
                background: brandPrimary,
                color: 'white',
                padding: '3.5rem 3rem 3rem',
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {/* Floating Pill */}
              <div
                style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '-15px',
                  background: brandDark,
                  padding: '0.6rem 1.2rem',
                  borderRadius: '30px',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transform: 'rotate(-4deg)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                }}
              >
                <span style={{ color: brandSecondary, fontSize: '1.2rem' }}>★</span> Free Forever
              </div>

              <h3 style={{ fontSize: '4.5rem', fontWeight: 900, margin: '1rem 0 0 0', lineHeight: 1, letterSpacing: '-0.05em' }}>
                2<span style={{ fontSize: '1.8rem', letterSpacing: '0' }}> GB</span>
              </h3>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2.5rem', marginTop: '0.5rem' }}>Free Plan</div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 2rem 0',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  flex: 1,
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.2rem',
                }}
              >
                {[
                  'Enterprise Encryption',
                  'Photos & 4K Video Streaming',
                  'AI Semantic Search',
                  'Interactive Map Heatmaps',
                  'Account & Personal Vaults',
                  'Uncompressed RAW & Video',
                  'Direct-to-Disk Folder Export',
                  'Zero Egress Bandwidth Fees',
                  '2 GB Free Forever',
                ].map((feat, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <div
                      style={{
                        background: 'white',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={brandPrimary} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Section: Position relative to hold the absolute toggle */}
            <div style={{ flex: 1, position: 'relative', display: 'flex', gap: '2.5rem', alignItems: 'flex-start' }}>
              {/* Toggle Row */}
              <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', zIndex: 10 }}>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: billingCycle === 'monthly' ? '#4B5563' : '#9CA3AF' }}>Monthly</span>
                <div
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  style={{ width: '54px', height: '30px', background: brandPrimary, borderRadius: '15px', position: 'relative', cursor: 'pointer' }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '4px',
                      left: billingCycle === 'yearly' ? '28px' : '4px',
                      transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                    }}
                  />
                </div>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: billingCycle === 'yearly' ? '#4B5563' : '#9CA3AF' }}>Annually</span>
              </div>

              <style>{`
                .right-pricing-layout { flex-wrap: nowrap; }
                .pricing-cards-grid { grid-template-columns: 1fr 1fr; }
                @media (max-width: 1000px) {
                  .right-pricing-layout { flex-wrap: wrap !important; }
                  .pricing-cards-grid { grid-template-columns: 1fr !important; }
                }
              `}</style>

              <div className="right-pricing-layout" style={{ display: 'flex', width: '100%', gap: '2.5rem', alignItems: 'flex-start' }}>
                {/* Left Column of Paid Section */}
                <div style={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column', gap: '2rem', paddingTop: '4rem' }}>
                  <h3 style={{ fontSize: '3.5rem', fontWeight: 900, margin: 0, letterSpacing: '-0.05em', color: brandDark, lineHeight: 0.9 }}>
                    paid<br />plans.
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[
                      { icon: '✓', text: 'Everything in Free' },
                      { icon: '+', text: 'Multi-User Team Seats' },
                      { icon: '+', text: 'Client Proofing & Reviews' },
                      { icon: '+', text: 'Guest Upload Dropboxes' },
                      { icon: '+', text: 'Face AI & Culling Studio' },
                      { icon: '+', text: 'Zero Egress Penalties' },
                      { icon: '+', text: 'Priority Edge Speed' },
                    ].map((pill, i) => (
                      <div
                        key={i}
                        style={{
                          background: brandPrimary,
                          color: 'white',
                          padding: '0.6rem 1rem 0.6rem 0.6rem',
                          borderRadius: '30px',
                          fontWeight: 800,
                          fontSize: '0.85rem',
                          display: 'flex',
                          gap: '0.6rem',
                          alignItems: 'center',
                          width: 'fit-content',
                        }}
                      >
                        <span
                          style={{
                            background: 'white',
                            color: brandPrimary,
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            fontWeight: 900,
                          }}
                        >
                          {pill.icon}
                        </span>
                        {pill.text}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Column of Paid Section: Cards Grid */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '4rem' }}>
                  <div className="pricing-cards-grid" style={{ display: 'grid', gap: '1.5rem' }}>
                    {/* Starter */}
                    <motion.div
                      whileHover={{ scale: 1.03, y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      onClick={() => onSelectPlan && onSelectPlan('personal')}
                      style={{ background: '#F4F5F7', borderRadius: '24px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', cursor: 'pointer', border: '2px solid transparent' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div style={{ color: brandPrimary, fontWeight: 900, fontSize: '1.3rem' }}>
                        Personal <span style={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 700 }}>1 account</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', letterSpacing: '-0.03em' }}>
                        50 <span style={{ fontSize: '1.1rem' }}>GB</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2.8rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginTop: '0.5rem', letterSpacing: '-0.05em' }}>
                        ${billingCycle === 'yearly' ? '4.40' : '4.90'} <span style={{ fontSize: '1rem', color: '#6B7280', letterSpacing: '0', fontWeight: 700 }}>/mo</span>
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                        <button className="btn btn-primary" style={{ width: '100%' }}>Sign Up</button>
                      </div>
                    </motion.div>

                    {/* Popular */}
                    <motion.div
                      whileHover={{ scale: 1.03, y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      onClick={() => onSelectPlan && onSelectPlan('family')}
                      style={{ background: '#F4F5F7', borderRadius: '24px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', cursor: 'pointer', border: '2px solid transparent' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div style={{ position: 'absolute', top: '-12px', right: '-12px', background: brandPrimary, color: 'white', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', boxShadow: '0 4px 10px rgba(124,58,237,0.3)' }}>♥</div>
                      <div style={{ color: brandPrimary, fontWeight: 900, fontSize: '1.3rem' }}>
                        Family <span style={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 700 }}>up to 5 members</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', letterSpacing: '-0.03em' }}>
                        200 <span style={{ fontSize: '1.1rem' }}>GB</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2.8rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginTop: '0.5rem', letterSpacing: '-0.05em' }}>
                        ${billingCycle === 'yearly' ? '11.60' : '12.90'} <span style={{ fontSize: '1rem', color: '#6B7280', letterSpacing: '0', fontWeight: 700 }}>/mo</span>
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                        <button className="btn btn-primary" style={{ width: '100%' }}>Sign Up</button>
                      </div>
                    </motion.div>

                    {/* Pro */}
                    <motion.div
                      whileHover={{ scale: 1.03, y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      onClick={() => onSelectPlan && onSelectPlan('pro')}
                      style={{ background: '#F4F5F7', borderRadius: '24px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', cursor: 'pointer', border: '2px solid transparent' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div style={{ color: brandPrimary, fontWeight: 900, fontSize: '1.3rem' }}>
                        Pro <span style={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 700 }}>up to 10 team seats</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', letterSpacing: '-0.03em' }}>
                        1 <span style={{ fontSize: '1.1rem' }}>TB</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2.8rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginTop: '0.5rem', letterSpacing: '-0.05em' }}>
                        ${billingCycle === 'yearly' ? '35.90' : '39.90'} <span style={{ fontSize: '1rem', color: '#6B7280', letterSpacing: '0', fontWeight: 700 }}>/mo</span>
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                        <button className="btn btn-primary" style={{ width: '100%' }}>Sign Up</button>
                      </div>
                    </motion.div>

                    {/* Studio */}
                    <motion.div
                      whileHover={{ scale: 1.03, y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      onClick={() => { window.location.href = 'mailto:jp@buzzwebmedia.com.au?subject=FileSharez%20Studio'; }}
                      style={{ background: '#F4F5F7', borderRadius: '24px', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', cursor: 'pointer', border: '2px solid transparent' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div style={{ color: brandPrimary, fontWeight: 900, fontSize: '1.3rem' }}>
                        Studio <span style={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 700 }}>custom teams</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', letterSpacing: '-0.03em' }}>
                        5+ <span style={{ fontSize: '1.1rem' }}>TB</span>
                      </div>
                      <div style={{ color: brandDark, fontWeight: 900, fontSize: '2.8rem', display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginTop: '0.5rem', letterSpacing: '-0.05em' }}>
                        Custom
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                        <button className="btn btn-primary" style={{ width: '100%' }}>Talk to us</button>
                      </div>
                    </motion.div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', color: '#6B7280', fontWeight: 700, fontSize: '0.9rem' }}>
                    Prices in AUD (inc. 10% GST). Need more storage?{' '}
                    <span style={{ color: brandPrimary, marginLeft: '0.3rem', cursor: 'pointer', borderBottom: `2px solid ${brandPrimary}` }}>
                      Talk to us.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
